import React from 'react';
import type { VehicleDerivative } from '../../types/manualsearch.types';
import { VehicleDerivativeCard } from './VehicleDerivativeCard';
import { Alert, Badge, LoadingSpinner } from '@shared/components';

interface DerivativeResultsProps {
    isLoading: boolean;
    hasSearched: boolean;
    derivatives: VehicleDerivative[];
    onSelect?: (derivative: VehicleDerivative) => void;
}

export const DerivativeResults: React.FC<DerivativeResultsProps> = ({
    isLoading = false,
    hasSearched,
    derivatives,
    onSelect,
}) => {
    const isFirst = !hasSearched && !isLoading && derivatives.length === 0;

    return (
        <div className="relative rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6 h-full">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-mp-foreground">Found Vehicles</h3>
                <Badge variant="outline">
                    {derivatives.length} vehicle{derivatives.length !== 1 ? 's' : ''} found
                </Badge>
            </div>

            {isFirst ? (
                <Alert variant="info" role="note">
                    <div className="flex flex-col items-start gap-2">
                        <p className="font-semibold">Vehicle Search</p>
                        <p>Please choose a method to search for vehicles.</p>
                    </div>
                </Alert>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 md:gap-5">
                    {isLoading && derivatives.length === 0 && (
                        <LoadingSpinner className="absolute left-0 right-0 top-0 bottom-0 p-4" />
                    )}
                    {!isLoading && derivatives.length === 0 && (
                        <div className="md:col-span-2 2xl:col-span-3">
                            <Alert variant="warning" role="status">
                                <div className="flex flex-col items-start gap-2">
                                    <p className="font-semibold">No Vehicles Found</p>
                                    <p>
                                        Unfortunately, there are no vehicles available at the moment from
                                        your manual vehicle derivative search.
                                    </p>
                                </div>
                            </Alert>
                        </div>
                    )}
                    {derivatives.map((derivative) => (
                        <VehicleDerivativeCard
                            key={(derivative as VehicleDerivative).id}
                            derivative={derivative as VehicleDerivative}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
